import React, { CSSProperties, ReactNode } from 'react';

import AniSlideUpBlur from '@/components/shared/animations/basic/AniSlideUpBlur';
import ButtonText from '@/components/shared/ui/buttons/ButtonText';

import theme from '@/styles/theme';

import VisuallyHiddenText from '../a11y/VisuallyHiddenText';
import { IconArrowRightRound } from '../icon';

type ColorType = 'darker' | 'lighter';
type AlignType = 'start' | 'center';

type Props = {
  title: React.ReactNode;
  description?: React.ReactNode;
  iconRight?: ReactNode;
  buttonWrapStyle?: CSSProperties;
  linkInfo?: { context?: string; title: string; url: string };
  align?: AlignType;
  color?: ColorType;
};

/*
 * 원본은 컨테이너에서 ${Title}, ${Description} 컴포넌트 셀렉터로 색을 내려줬다.
 * 유틸리티는 모두 !important 라서 같은 속성(color)을 두 번 내보내면 순서 싸움이 나므로,
 * color prop 의 승자를 JS 에서 미리 정해 각 요소에 클래스 하나씩만 붙인다.
 */
const TITLE_COLOR_CLASS: Record<ColorType, string> = {
  darker: 'text-apps-coreGray01',
  lighter: 'text-apps-brandGrayWhite',
};

const DESCRIPTION_COLOR_CLASS: Record<ColorType, string> = {
  darker: 'text-apps-brandGray200',
  lighter: 'text-apps-brandGray900',
};

const ALIGN_CLASS: Record<AlignType, string> = {
  start: 'justify-start',
  center: 'justify-center',
};

/*
 * 원본 ButtonWrap 은 emotion 클래스로 `padding: 8px 0`(lg 는 16px 0)을 주고,
 * caller 가 넘긴 buttonWrapStyle 인라인 style 이 그것을 덮어쓰는 구조였다
 * (인라인 style > 비-important 클래스). 실제로 AboutNewsRoomHeader 가
 * `padding: isDesktop ? '12px 0' : '8px 0'` 을 넘겨 12px 로 렌더되고 있었다.
 * Tailwind 유틸리티는 전부 !important 라 인라인 style 을 이겨버리므로,
 * caller 가 세로 padding 을 지정하면 유틸리티를 붙이지 않아 원래 승자를 유지한다.
 */
function buttonWrapClass(style?: CSSProperties) {
  const hasVerticalPaddingOverride =
    style !== undefined &&
    (style.padding !== undefined ||
      style.paddingBlock !== undefined ||
      style.paddingTop !== undefined ||
      style.paddingBottom !== undefined);

  return hasVerticalPaddingOverride ? '' : 'py-[8px] lg:py-[16px]';
}

export default function SectionTitle({
  title,
  linkInfo,
  description,
  iconRight = (
    <IconArrowRightRound color={theme`colors.apps.coreGrayBlack`} size={12} />
  ),
  buttonWrapStyle,
  align = 'start',
  color = 'darker',
}: Props) {
  return (
    <div>
      <AniSlideUpBlur delay={0.2}>
        <div className={`flex items-end gap-[24px] ${ALIGN_CLASS[align]}`}>
          <h2
            className={`text-[clamp(32px,2.7083vw,52px)] font-bold leading-[120%] tracking-[-0.64px] lg:leading-[140%] lg:tracking-[-1.04px] ${TITLE_COLOR_CLASS[color]}`}
          >
            {title}
          </h2>
          {linkInfo && (
            <div
              className={buttonWrapClass(buttonWrapStyle)}
              style={buttonWrapStyle}
            >
              <ButtonText
                variant="underline"
                href={linkInfo.url}
                iconRight={iconRight}
              >
                {linkInfo.context && (
                  <VisuallyHiddenText>{linkInfo.context} </VisuallyHiddenText>
                )}
                {linkInfo.title}
              </ButtonText>
            </div>
          )}
        </div>
        {/*
         * 원본 Description 은 `font-weight: 500px` 로 선언되어 있었다.
         * 500px 은 font-weight 의 유효값이 아니라 브라우저가 선언을 버렸으므로,
         * 겉모습을 그대로 유지하기 위해 font-weight 유틸리티를 붙이지 않는다.
         */}
        {description && (
          <h3
            className={`mt-[16px] leading-[140%] tracking-[-0.32px] lg:text-[20px] lg:leading-[160%] lg:tracking-[-0.4px] ${DESCRIPTION_COLOR_CLASS[color]}`}
          >
            {description}
          </h3>
        )}
      </AniSlideUpBlur>
    </div>
  );
}
